/** Tipos y funciones puras de categorías del catálogo (sin I/O). */
import type { Product } from './productTypes';

/** Alcance de la categoría en el catálogo (producto físico, servicio o ambos). */
export type CategoryAppliesTo = 'product' | 'service' | 'both';

export const normalizeCategoryAppliesTo = (v: string | null | undefined): CategoryAppliesTo => {
  if (v === 'product' || v === 'service' || v === 'both') return v;
  return 'product';
};

export const categoryAppliesToDraftItemType = (
  appliesTo: CategoryAppliesTo,
  itemType: 'physical' | 'service'
): boolean => {
  if (appliesTo === 'both') return true;
  if (itemType === 'service') return appliesTo === 'service';
  return appliesTo === 'product';
};

export type CategoryRecord = {
  id: string;
  slug: string;
  name: string;
  organizationId?: string | null;
  isActive: boolean;
  appliesTo: CategoryAppliesTo;
  /** Catálogo público: requiere confirmación de mayoría de edad (tabaco/alcohol). */
  isRestricted: boolean;
  sortOrder: number;
  color?: string | null;
  parentId?: string | null;
  code?: string | null;
  defaultMarginPercent?: number | null;
  description?: string | null;
  imageUrl?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  deletedAt?: string | null;
};

export type CategoryOption = {
  id: string;
  label: string;
  color?: string | null;
};

/** Labels para slugs legacy/seed en reportes y exports sin categoría local. */
const LEGACY_CATEGORY_LABELS: Record<string, string> = {
  snacks: 'Golosinas',
  cigarettes: 'Cigarrillos',
  drinks: 'Bebidas',
  others: 'Otros',
  'cat-snacks': 'Golosinas',
  'cat-cigarettes': 'Cigarrillos',
  'cat-drinks': 'Bebidas',
  'cat-others': 'Otros',
};

const categoryAliasEntries = [
  ['cat-snacks', ['snacks', 'golosinas', 'cat-snacks']],
  ['cat-drinks', ['drinks', 'bebidas', 'cat-drinks']],
  ['cat-cigarettes', ['cigarettes', 'cigarrillos', 'cat-cigarettes']],
  ['cat-others', ['others', 'otros', 'cat-others']],
] as const satisfies readonly [string, readonly string[]][];

const categoryAliasLookup = new Map<string, string>();
const categoryAliasesById = new Map<string, string[]>();

for (const [id, aliases] of categoryAliasEntries) {
  const normalizedAliases = Array.from(new Set([id, ...aliases].map((alias) => alias.trim().toLowerCase())));
  categoryAliasesById.set(id, normalizedAliases);
  for (const alias of normalizedAliases) {
    categoryAliasLookup.set(alias, id);
  }
}

export const normalizeProductCategoryId = (categoryId?: string | null): string => {
  const normalized = categoryId?.trim().toLowerCase();
  if (!normalized) {
    return '';
  }

  const direct = categoryAliasLookup.get(normalized);
  if (direct != null) {
    return direct;
  }

  const withoutSeed = normalized.startsWith('seed-') ? normalized.slice(5) : null;
  if (withoutSeed != null) {
    const mapped = categoryAliasLookup.get(withoutSeed);
    if (mapped != null) {
      return mapped;
    }
  }

  /** Sin alias conocido: el ID típico de productos con prefijo `seed-`. */
  return withoutSeed ?? normalized;
};

export const getCategoryFilterValues = (categoryId: Product['categoryId'] | 'all') => {
  if (categoryId === 'all') {
    return [];
  }

  const normalized = normalizeProductCategoryId(categoryId);
  if (!normalized) {
    return [];
  }

  return categoryAliasesById.get(normalized) ?? [normalized];
};

/**
 * Variantes RAW en SQLite (`products.category_id`) alineadas con el ID elegido en UI.
 * Incluye `seed-*` porque el catálogo local / sync puede persistir ese prefijo frente al ID público de la tabla `categories`.
 */
export const sqlCategoryComparableValuesForFilter = (
  categoryId: Product['categoryId'] | 'all',
): string[] => {
  if (categoryId === 'all') return [];
  const fromTaxonomy = getCategoryFilterValues(categoryId);
  const idRaw = typeof categoryId === 'string' ? categoryId.trim().toLowerCase() : '';
  const out = new Set<string>();
  for (const raw of [...fromTaxonomy, idRaw]) {
    const t = String(raw).toLowerCase().trim();
    if (!t) continue;
    out.add(t);
    if (!t.startsWith('seed-')) {
      out.add(`seed-${t}`);
    } else {
      out.add(t.slice(5));
    }
  }
  return Array.from(out);
};

export const productMatchesCategoryFilter = (
  productCategoryId: string | null | undefined,
  filter: Product['categoryId'] | 'all'
) => {
  if (filter === 'all') {
    return true;
  }

  const normalizedProductCategoryId = normalizeProductCategoryId(productCategoryId);
  return getCategoryFilterValues(filter).includes(normalizedProductCategoryId);
};

/** `cat-<uuid>` (id generado) o hex largo: no es texto mostrable. */
const CATEGORY_ID_LIKE_RE = /^[0-9a-f]{8}-[0-9a-f-]{20,}$|^[0-9a-f]{24,}$/;

/**
 * Texto de categoría solo para mostrar (sin prefijos de backend como `seed-`/`cat-`,
 * mapeo a nombres del catálogo). Devuelve null cuando el valor es un id opaco
 * (uuid) — ahí corresponde un fallback del caller, no el id crudo.
 */
export function getCategoryDisplayLabel(raw: string | null | undefined): string | null {
  if (raw == null || String(raw).trim() === '') return null;
  let s = String(raw).trim().toLowerCase();
  if (s.startsWith('seed-')) {
    s = s.slice(5);
  }
  const canonical = normalizeProductCategoryId(s) || s;
  const legacy = LEGACY_CATEGORY_LABELS[canonical] ?? LEGACY_CATEGORY_LABELS[s];
  if (legacy) return legacy;
  // Slugs seed/locales vienen como `cat-almacen` — mostrar «Almacen», no el slug.
  const display = canonical.startsWith('cat-') ? canonical.slice(4) : canonical;
  if (!display || CATEGORY_ID_LIKE_RE.test(display)) return null;
  return display
    .replace(/[-_]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
